import type { SubmissionDoc } from '@orderlymeds/measurements-schemas';
import { useQuery } from '@tanstack/react-query';
import { useApiFetch } from './ApiProvider';

// Query keys
export const submissionKeys = {
	all: (projectId: string) => ['projects', projectId, 'submissions'] as const,
	list: (projectId: string, params?: SubmissionListParams) =>
		['projects', projectId, 'submissions', params] as const,
	detail: (projectId: string, subId: string) =>
		['projects', projectId, 'submissions', subId] as const,
};

export interface SubmissionListParams {
	form_id?: string;
	limit?: number;
	after?: string;
}

/** Lists submissions with optional form_id filter and cursor pagination. */
export function useSubmissions(projectId: string, params?: SubmissionListParams) {
	const apiFetch = useApiFetch();
	return useQuery({
		queryKey: submissionKeys.list(projectId, params),
		queryFn: () => {
			const search = new URLSearchParams();
			if (params?.form_id) search.set('form_id', params.form_id);
			if (params?.limit != null) search.set('limit', String(params.limit));
			if (params?.after) search.set('after', params.after);
			const qs = search.toString();
			const url = `/api/admin/projects/${projectId}/submissions${qs ? `?${qs}` : ''}`;
			return apiFetch<SubmissionDoc[]>(url);
		},
		enabled: !!projectId,
		staleTime: 15_000,
	});
}

/** Fetches a single submission by ID. */
export function useSubmission(projectId: string, subId: string) {
	const apiFetch = useApiFetch();
	return useQuery({
		queryKey: submissionKeys.detail(projectId, subId),
		queryFn: () => apiFetch<SubmissionDoc>(`/api/admin/projects/${projectId}/submissions/${subId}`),
		enabled: !!projectId && !!subId,
		staleTime: 30_000,
	});
}
